import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import client, { API } from "@/api/client";
import { Users, TrendingUp, Download, Megaphone, Ban, KeyRound, UserPlus, Trash2, Upload, ImageIcon, X, Edit3, Save, CalendarDays, Lock, Unlock, ShieldCheck, ShieldOff, Mail, MailX, MessageCircle, Send, Inbox } from "lucide-react";
import { toast, Toaster } from "sonner";

const TABS = ["Overview", "Students", "Content", "Outlook", "Q&A", "Certificates", "Downloads", "Announcements"];

export default function AdminPanel() {
  const [tab, setTab] = useState("Overview");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [anns, setAnns] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "student" });
  const [ann, setAnn] = useState({ title: "", body: "" });

  const loadAll = async () => {
    const [u, s, d, a] = await Promise.all([
      client.get("/admin/users"),
      client.get("/admin/analytics"),
      client.get("/admin/downloads"),
      client.get("/announcements"),
    ]);
    setUsers(u.data.users); setStats(s.data);
    setLogs(d.data.downloads); setAnns(a.data.announcements);
  };
  useEffect(() => { loadAll(); }, []);

  const doInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/admin/users/invite", invite);
      if (data.email_sent) {
        toast.success(`Invite email sent to ${data.email}. Temp pw: ${data.temp_password}`, { duration: 30000 });
      } else {
        toast(`Invited ${data.email}. Send this temp pw manually: ${data.temp_password}`, { duration: 60000, description: `Email not sent — ${data.email_reason}` });
      }
      setInvite({ email: "", name: "", role: "student" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const suspend = async (id) => {
    await client.post(`/admin/users/${id}/suspend`);
    loadAll();
  };
  const revoke = async (id) => {
    await client.post(`/admin/users/${id}/revoke`);
    toast.success("Access revoked. Their session is now dead.");
    loadAll();
  };
  const resetPw = async (id, email) => {
    if (!window.confirm(`Reset password for ${email}? They'll be forced to set a new one at next login.`)) return;
    try {
      const { data } = await client.post(`/admin/users/${id}/reset-password`);
      if (data.email_sent) {
        toast.success(`Reset email sent to ${data.email} (temp: ${data.temp_password})`, { duration: 30000 });
      } else {
        toast(`New temp password for ${data.email}: ${data.temp_password}`, { duration: 60000, description: `Email not sent — ${data.email_reason}` });
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Permanently remove this student?")) return;
    await client.delete(`/admin/users/${id}`);
    loadAll();
  };

  const postAnn = async (e) => {
    e.preventDefault();
    await client.post("/admin/announcements", ann);
    setAnn({ title: "", body: "" });
    loadAll();
    toast.success("Announcement pushed to all students");
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <p className="overline">Admin Control</p>
      <h1 className="font-display text-5xl mb-8">The Boardroom Panel</h1>
      <div className="flex gap-6 border-b border-[#1f1f1f] mb-10">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`pb-3 uppercase tracking-widest text-xs transition ${
              tab === t ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-neutral-500 hover:text-neutral-200"
            }`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <div data-testid="admin-overview">
          <UnlockToggle />
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            <Stat label="Active Students" value={stats.stats.students} icon={<Users size={16} />} />
            <Stat label="Lessons Completed" value={stats.stats.lessons_completed} icon={<TrendingUp size={16} />} />
            <Stat label="PDF Downloads" value={stats.stats.downloads} icon={<Download size={16} />} />
          </div>
          <h3 className="font-display text-2xl mb-4">Recent Logins</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left overline border-b border-[#1f1f1f]">
              <th className="py-3">Email</th><th>When</th></tr></thead>
            <tbody>{stats.recent_logins.map((l, i) => (
              <tr key={i} className="border-b border-[#111]"><td className="py-2">{l.email}</td>
                <td className="text-neutral-500">{new Date(l.at).toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === "Students" && (
        <div data-testid="admin-students">
          <EmailStatusBanner />
          <form onSubmit={doInvite} className="grid md:grid-cols-4 gap-3 mb-10 border border-[#1f1f1f] p-5">
            <input required placeholder="Full name" className="input-boardroom"
              value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
              data-testid="invite-name" />
            <input required type="email" placeholder="Email" className="input-boardroom"
              value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              data-testid="invite-email" />
            <select className="input-boardroom" value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value })} data-testid="invite-role">
              <option value="student">Student</option><option value="admin">Admin</option>
            </select>
            <button type="submit" className="btn-gold" data-testid="invite-submit">
              <UserPlus size={13} className="inline mr-2" /> Invite
            </button>
          </form>
          <table className="w-full text-sm">
            <thead><tr className="text-left overline border-b border-[#1f1f1f]">
              <th className="py-3">Name</th><th>Email</th><th>Role</th>
              <th>Progress</th><th>Status</th><th className="text-right pr-2">Actions</th></tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.id} className="border-b border-[#111]" data-testid={`user-row-${u.email}`}>
                <td className="py-3">{u.name}</td>
                <td className="text-neutral-400">{u.email}</td>
                <td><span className={`text-[10px] uppercase tracking-widest ${u.role === "admin" ? "text-[#D4AF37]" : "text-neutral-400"}`}>{u.role}</span></td>
                <td className="text-neutral-400">{u.lessons_done} lessons · {u.downloads} PDFs</td>
                <td>{u.suspended ? <span className="text-red-400 text-xs uppercase tracking-widest">Suspended</span> : <span className="text-emerald-500 text-xs uppercase tracking-widest">Active</span>}</td>
                <td className="text-right space-x-2 pr-2">
                  <button onClick={() => resetPw(u.id, u.email)} className="text-xs text-neutral-400 hover:text-[#D4AF37]" title="Reset password" data-testid={`reset-pw-${u.email}`}><KeyRound size={14} className="inline" style={{ transform: "rotate(-45deg)" }}/></button>
                  <button onClick={() => suspend(u.id)} className="text-xs text-neutral-400 hover:text-[#D4AF37]" data-testid={`suspend-${u.email}`}><Ban size={14} className="inline" /></button>
                  <button onClick={() => revoke(u.id)} className="text-xs text-neutral-400 hover:text-[#D4AF37]" title="Revoke session" data-testid={`revoke-${u.email}`}><KeyRound size={14} className="inline" /></button>
                  <button onClick={() => del(u.id)} className="text-xs text-neutral-400 hover:text-red-400" data-testid={`delete-${u.email}`}><Trash2 size={14} className="inline" /></button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === "Content" && <ContentTab />}
      {tab === "Outlook" && <OutlookTab />}
      {tab === "Q&A" && <QATab />}
      {tab === "Certificates" && <CertificatesTab />}

      {tab === "Downloads" && (
        <div data-testid="admin-downloads">
          <h3 className="font-display text-2xl mb-4">Download Log</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left overline border-b border-[#1f1f1f]">
              <th className="py-3">User</th><th>Type</th><th>Module</th><th>When</th></tr></thead>
            <tbody>{logs.map(l => (
              <tr key={l.id} className="border-b border-[#111]">
                <td className="py-2 text-neutral-300">{l.email}</td>
                <td><span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{l.kind}</span></td>
                <td className="text-neutral-500">{l.module_id || "—"}</td>
                <td className="text-neutral-500">{new Date(l.at).toLocaleString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === "Announcements" && (
        <div data-testid="admin-announcements">
          <form onSubmit={postAnn} className="border border-[#1f1f1f] p-5 mb-10">
            <input required placeholder="Title" className="input-boardroom mb-3"
              value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })}
              data-testid="ann-title" />
            <textarea required placeholder="Body" rows={4} className="input-boardroom mb-3 resize-none"
              value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })}
              data-testid="ann-body" />
            <button type="submit" className="btn-gold" data-testid="ann-post">
              <Megaphone size={13} className="inline mr-2" /> Push to all students
            </button>
          </form>
          <div className="space-y-4">
            {anns.map(a => (
              <div key={a.id} className="border border-[#1f1f1f] p-5">
                <div className="flex justify-between">
                  <p className="font-display text-2xl text-[#D4AF37]">{a.title}</p>
                  <button onClick={async () => { await client.delete(`/admin/announcements/${a.id}`); loadAll(); }}
                          className="text-xs text-neutral-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
                <p className="text-sm text-neutral-300 mt-2 whitespace-pre-line">{a.body}</p>
                <p className="text-[10px] text-neutral-600 mt-3 uppercase tracking-widest">{new Date(a.at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

const Stat = ({ label, value, icon }) => (
  <div className="border border-[#1f1f1f] p-6 bg-[#0d0d0d]">
    <div className="flex items-center gap-2 text-[#D4AF37]">{icon}<p className="overline">{label}</p></div>
    <p className="font-display text-5xl mt-3 text-white">{value}</p>
  </div>
);

function QATab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/qa/unanswered");
      setQuestions(data.questions);
    } catch (e) {
      toast.error("Failed to load Q&A");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const reply = async (q) => {
    const body = (drafts[q.id] || "").trim();
    if (!body) return;
    setSending(s => ({ ...s, [q.id]: true }));
    try {
      await client.post(`/admin/qa/${q.id}/reply`, { body });
      toast.success(`Reply sent to ${q.author_name}`);
      setDrafts(d => ({ ...d, [q.id]: "" }));
      load();
    } catch (e) {
      toast.error("Failed to send reply");
    }
    setSending(s => ({ ...s, [q.id]: false }));
  };

  const dismiss = async (q) => {
    if (!window.confirm(`Delete this question from ${q.author_name}? This removes it and any replies.`)) return;
    try {
      await client.delete(`/admin/qa/${q.id}`);
      toast.success("Question removed");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Group by module for a scannable inbox layout
  const grouped = questions.reduce((acc, q) => {
    const key = q.module_title || "—";
    (acc[key] = acc[key] || []).push(q);
    return acc;
  }, {});

  return (
    <div data-testid="admin-qa">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <p className="overline flex items-center gap-2"><Inbox size={13} className="text-[#D4AF37]" /> Unanswered Q&amp;A</p>
          <h3 className="font-display text-3xl mt-1">Student Inbox</h3>
          <p className="text-sm text-neutral-500 mt-1">Every question a student posts appears here until you reply. Nothing slips through.</p>
        </div>
        <button onClick={load} className="btn-outline-gold text-xs" data-testid="qa-refresh">Refresh</button>
      </div>

      {loading && <p className="text-neutral-500 text-sm">Loading…</p>}
      {!loading && questions.length === 0 && (
        <div className="border border-[#1f1f1f] p-10 text-center" data-testid="qa-empty">
          <MessageCircle size={28} className="mx-auto text-[#D4AF37]/60 mb-3" />
          <p className="font-display text-2xl text-[#D4AF37]">Inbox Zero</p>
          <p className="text-sm text-neutral-500 mt-2">No unanswered questions right now.</p>
        </div>
      )}

      <div className="space-y-10">
        {Object.entries(grouped).map(([modTitle, qs]) => (
          <section key={modTitle} data-testid={`qa-group-${modTitle}`}>
            <div className="flex items-center gap-3 mb-4">
              <p className="overline text-[#D4AF37]">◇ {modTitle}</p>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                {qs.length} question{qs.length === 1 ? "" : "s"}
              </span>
              <div className="flex-1 border-t border-[#1f1f1f]" />
            </div>
            <div className="space-y-5">
              {qs.map((q) => (
                <div key={q.id} className="border border-[#1f1f1f] bg-[#0d0d0d] p-5" data-testid={`qa-item-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm">
                        <span className="text-white font-semibold">{q.author_name}</span>
                        <span className="text-neutral-500 text-xs ml-2">{q.author_email}</span>
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                        {q.lesson_title} · {new Date(q.at).toLocaleString()}
                      </p>
                    </div>
                    <button onClick={() => dismiss(q)}
                            className="text-xs text-neutral-500 hover:text-red-400"
                            data-testid={`qa-dismiss-${q.id}`} title="Delete question">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-neutral-200 text-[15px] leading-relaxed font-body border-l-2 border-[#333] pl-4 mb-4 whitespace-pre-line">
                    {q.body}
                  </p>
                  <textarea
                    value={drafts[q.id] || ""}
                    onChange={(e) => setDrafts(d => ({ ...d, [q.id]: e.target.value }))}
                    rows={3}
                    placeholder="Write your reply…"
                    className="input-boardroom font-body resize-none mb-3"
                    data-testid={`qa-reply-input-${q.id}`}
                  />
                  <div className="flex items-center gap-3">
                    <button onClick={() => reply(q)}
                            disabled={sending[q.id] || !(drafts[q.id] || "").trim()}
                            className="btn-gold text-xs"
                            data-testid={`qa-reply-send-${q.id}`}>
                      <Send size={12} className="inline mr-2" />
                      {sending[q.id] ? "Sending…" : "Send Reply"}
                    </button>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-600">
                      Reply appears under the lesson's Q&amp;A as ◇ Boardroom
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}


function ContentTab() {
  const [levels, setLevels] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [module, setModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState("");
  const [images, setImages] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    client.get("/curriculum").then((r) => setLevels(r.data.levels));
  }, []);

  useEffect(() => {
    if (!selectedModule) { setModule(null); setSelectedLesson(""); return; }
    client.get(`/modules/${selectedModule}`).then((r) => {
      setModule(r.data);
      if (r.data.lessons.length > 0) setSelectedLesson(r.data.lessons[0].id);
    });
  }, [selectedModule]);

  useEffect(() => {
    if (selectedLesson && module) {
      const l = module.lessons.find((x) => x.id === selectedLesson);
      setImages(l?.images || []);
    }
  }, [selectedLesson, module]);

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file || !selectedLesson) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      const { data } = await client.post(
        `/admin/lessons/${selectedLesson}/images`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImages([...images, data]);
      setCaption("");
      setFile(null);
      document.getElementById("chart-upload-file").value = "";
      toast.success("Chart uploaded — visible to all students");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeImage = async (imgId) => {
    if (!window.confirm("Remove this chart from the lesson?")) return;
    await client.delete(`/admin/lessons/${selectedLesson}/images/${imgId}`);
    setImages(images.filter((i) => i.id !== imgId));
    toast.success("Chart removed");
  };

  return (
    <div data-testid="admin-content">
      <SignatureSection />

      <div className="gold-divider mb-10" />

      <p className="overline mb-4">Lesson Charts</p>
      <p className="text-sm text-neutral-500 mb-6 max-w-2xl">
        Attach chart screenshots and diagrams to any lesson. Uploads are stored in Emergent object storage and served through an authenticated URL — only signed-in students can view them.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="overline mb-2 block">Module</label>
          <select className="input-boardroom" value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  data-testid="content-module-select">
            <option value="">— Select a module —</option>
            {levels.map((lv) => (
              <optgroup key={lv.level} label={`Level ${lv.level} · ${lv.title}`}>
                {lv.modules.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="overline mb-2 block">Lesson</label>
          <select className="input-boardroom" value={selectedLesson}
                  onChange={(e) => setSelectedLesson(e.target.value)}
                  disabled={!module}
                  data-testid="content-lesson-select">
            {(module?.lessons || []).map((l, i) => (
              <option key={l.id} value={l.id}>{String(i+1).padStart(2, "0")} · {l.title}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedLesson && (
        <>
          <LessonEditor lessonId={selectedLesson} module={module}
            onSaved={(updated) => {
              setModule((m) => ({ ...m, lessons: m.lessons.map(l => l.id === updated.id ? { ...l, ...updated } : l) }));
            }} />

          <div className="gold-divider my-8" />

          <form onSubmit={doUpload} className="border border-[#1f1f1f] p-5 mb-8 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
                data-testid="upload-form">
            <div>
              <label className="overline mb-2 block">Chart Image</label>
              <input id="chart-upload-file" required type="file"
                     accept="image/png,image/jpeg,image/webp,image/gif"
                     onChange={(e) => setFile(e.target.files[0])}
                     data-testid="upload-file"
                     className="input-boardroom file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#D4AF37] file:text-black file:uppercase file:tracking-widest file:text-[10px]" />
            </div>
            <div>
              <label className="overline mb-2 block">Caption (optional)</label>
              <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
                     placeholder="e.g., EURUSD H4 order block"
                     data-testid="upload-caption"
                     className="input-boardroom" />
            </div>
            <button type="submit" disabled={!file || uploading} className="btn-gold"
                    data-testid="upload-submit">
              <Upload size={13} className="inline mr-2" /> {uploading ? "Uploading…" : "Upload"}
            </button>
          </form>

          <p className="overline mb-4">Attached Charts ({images.length})</p>
          {images.length === 0 && (
            <div className="border border-dashed border-[#1f1f1f] p-10 text-center text-neutral-600 text-sm">
              <ImageIcon size={24} className="mx-auto mb-3 text-neutral-700" />
              No charts attached yet. Upload one above.
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((img) => (
              <figure key={img.id} className="border border-[#1f1f1f] bg-[#0a0a0a] p-3 relative group"
                      data-testid={`content-image-${img.id}`}>
                <button onClick={() => removeImage(img.id)}
                        className="absolute top-2 right-2 bg-black/80 border border-[#333] hover:border-red-500 text-neutral-400 hover:text-red-400 p-1 transition"
                        data-testid={`content-image-delete-${img.id}`}>
                  <X size={14} />
                </button>
                <img src={`${API}/files/${img.id}?auth=${localStorage.getItem("boardroom_token") || ""}`}
                     alt={img.caption} className="w-full h-40 object-cover" draggable={false} />
                <figcaption className="mt-2 text-[11px] text-neutral-500 truncate">
                  {img.caption || img.original_filename}
                </figcaption>
              </figure>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SignatureSection() {  const [state, setState] = useState({ configured: false, signer_name: "", signer_title: "" });
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const load = async () => {
    const { data } = await client.get("/admin/signature");
    setState(data);
    setSignerName(data.signer_name || "");
    setSignerTitle(data.signer_title || "");
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("signer_name", signerName);
      fd.append("signer_title", signerTitle);
      if (file) fd.append("file", file);
      await client.post("/admin/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate signature saved");
      setFile(null);
      const f = document.getElementById("sig-upload-file");
      if (f) f.value = "";
      setPreviewKey((k) => k + 1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save signature");
    } finally { setBusy(false); }
  };

  const removeSig = async () => {
    if (!window.confirm("Remove the signature image? (Signer name/title will remain.)")) return;
    await client.delete("/admin/signature");
    setPreviewKey((k) => k + 1);
    await load();
    toast.success("Signature image removed");
  };

  return (
    <section className="mb-10" data-testid="signature-section">
      <p className="overline mb-2">Certificate Signature</p>
      <h3 className="font-display text-3xl mb-3">Your Signature on Every Certificate</h3>
      <p className="text-sm text-neutral-500 mb-6 max-w-2xl">
        Upload a transparent PNG of your handwritten signature (recommended: white or gold ink on
        transparent background, ~600×200px). It will appear above the signature line on every
        Boardroom certificate a student downloads. The name and title below the line default to
        <em> &quot;The Boardroom · Founder&quot;</em> unless you set them here.
      </p>

      <form onSubmit={save} className="border border-[#1f1f1f] p-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="overline mb-2 block">Signer Name</label>
          <input type="text" value={signerName}
                 onChange={(e) => setSignerName(e.target.value)}
                 placeholder="e.g., Aleksy Novak"
                 data-testid="signer-name"
                 className="input-boardroom" />
        </div>
        <div>
          <label className="overline mb-2 block">Signer Title</label>
          <input type="text" value={signerTitle}
                 onChange={(e) => setSignerTitle(e.target.value)}
                 placeholder="e.g., Founder, The Boardroom"
                 data-testid="signer-title"
                 className="input-boardroom" />
        </div>
        <div className="md:col-span-2">
          <label className="overline mb-2 block">Signature Image (PNG with transparency preferred)</label>
          <input id="sig-upload-file" type="file"
                 accept="image/png,image/jpeg,image/webp"
                 onChange={(e) => setFile(e.target.files[0])}
                 data-testid="signature-file"
                 className="input-boardroom file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#D4AF37] file:text-black file:uppercase file:tracking-widest file:text-[10px]" />
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
          <button type="submit" disabled={busy} className="btn-gold" data-testid="signature-save">
            {busy ? "Saving…" : "Save Signature"}
          </button>
          {state.configured && (
            <button type="button" onClick={removeSig} className="btn-outline-gold" data-testid="signature-remove">
              <X size={12} className="inline mr-1" /> Remove Image
            </button>
          )}
          {state.configured && (
            <span className="text-[11px] text-neutral-500 uppercase tracking-widest ml-auto">
              ◇ Signature active
            </span>
          )}
        </div>

        {state.configured && (
          <div className="md:col-span-2 mt-2">
            <p className="overline mb-3">Live Preview</p>
            <div className="border border-[#1f1f1f] bg-black p-6 flex flex-col items-center">
              <img
                key={previewKey}
                src={`${API}/admin/signature/image?auth=${localStorage.getItem("boardroom_token") || ""}&v=${previewKey}`}
                alt="Signature"
                data-testid="signature-preview"
                className="max-h-24 max-w-[280px] object-contain"
              />
              <div className="w-72 h-px bg-[#AA8A25]/70 mt-2" />
              <p className="font-display italic text-white mt-2">{signerName || "The Boardroom"}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#AA8A25]">
                {signerTitle || "Founder, The Boardroom"}
              </p>
            </div>
          </div>
        )}
      </form>
    </section>
  );
}

function UnlockToggle() {
  const [sequential, setSequential] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get("/admin/settings/unlock").then((r) => setSequential(r.data.sequential));
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await client.put("/admin/settings/unlock", { sequential: !sequential });
      setSequential(data.sequential);
      toast.success(data.sequential
        ? "Sequential unlock ON — students must complete each module in order."
        : "Sequential unlock OFF — every module open to all students.");
    } catch (e) {
      toast.error("Failed to update setting");
    } finally { setBusy(false); }
  };

  if (sequential === null) return null;

  return (
    <section className="border border-[#1f1f1f] p-6 mb-10 flex items-start justify-between gap-6"
             data-testid="unlock-toggle-section">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 mb-1 text-[#D4AF37]">
          {sequential ? <Lock size={14} /> : <Unlock size={14} />}
          <p className="overline">Sequential Module Unlock</p>
        </div>
        <p className="font-display text-2xl">
          {sequential ? "ON — modules unlock in order" : "OFF — full access to every module"}
        </p>
        <p className="text-sm text-neutral-500 mt-2 font-body">
          When ON, students must complete every lesson in a module AND score at least 60% on its quiz
          before the next module unlocks. Admins bypass this rule entirely.
        </p>
      </div>
      <button onClick={toggle} disabled={busy}
              data-testid="unlock-toggle-btn"
              className={`relative w-16 h-8 rounded-full transition ${sequential ? "bg-[#D4AF37]" : "bg-[#1f1f1f] border border-[#333]"} ${busy ? "opacity-50" : ""}`}
              aria-pressed={sequential}>
        <span className={`absolute top-1 left-1 h-6 w-6 rounded-full transition-transform ${sequential ? "translate-x-8 bg-black" : "translate-x-0 bg-[#D4AF37]"}`} />
      </button>
    </section>
  );
}

function CertificatesTab() {
  const [certs, setCerts] = useState([]);

  const load = async () => {
    const { data } = await client.get("/admin/certificates");
    setCerts(data.certificates);
  };
  useEffect(() => { load(); }, []);

  const revoke = async (code) => {
    if (!window.confirm(`Revoke certificate ${code}? The public verify page will show REVOKED immediately.`)) return;
    await client.post(`/admin/certificates/${code}/revoke`);
    toast.success("Certificate revoked");
    load();
  };
  const restore = async (code) => {
    await client.post(`/admin/certificates/${code}/restore`);
    toast.success("Certificate restored to valid status");
    load();
  };

  const origin = window.location.origin;

  return (
    <div data-testid="admin-certificates">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} className="text-[#D4AF37]" />
        <p className="overline">Issued Certificates ({certs.length})</p>
      </div>
      <h3 className="font-display text-3xl mb-3">Boardroom Certificates</h3>
      <p className="text-sm text-neutral-500 mb-8 max-w-2xl">
        Every certificate has a unique code and a public verify page. Revoke here if a student breaks the
        Boardroom's terms — the verify page instantly reflects the change.
      </p>

      {certs.length === 0 ? (
        <div className="border border-dashed border-[#1f1f1f] p-16 text-center text-neutral-600 text-sm">
          No certificates issued yet. They appear here the moment a student downloads theirs.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left overline border-b border-[#1f1f1f]">
              <th className="py-3">Code</th><th>Recipient</th><th>Issued</th><th>Status</th>
              <th className="text-right pr-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {certs.map((c) => (
              <tr key={c.code} className="border-b border-[#111]" data-testid={`cert-row-${c.code}`}>
                <td className="py-3 font-mono text-[#D4AF37]">{c.code}</td>
                <td className="text-neutral-300">{c.name}</td>
                <td className="text-neutral-500">{new Date(c.issued_at).toLocaleDateString()}</td>
                <td>
                  {c.revoked ? (
                    <span className="text-red-400 text-xs uppercase tracking-widest">Revoked</span>
                  ) : (
                    <span className="text-emerald-500 text-xs uppercase tracking-widest">Valid</span>
                  )}
                </td>
                <td className="text-right space-x-3 pr-2">
                  <a href={`${origin}/verify/${c.code}`} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-neutral-400 hover:text-[#D4AF37]"
                     data-testid={`cert-preview-${c.code}`}>Preview ↗</a>
                  {c.revoked ? (
                    <button onClick={() => restore(c.code)} className="text-xs text-neutral-400 hover:text-emerald-400"
                            title="Restore" data-testid={`cert-restore-${c.code}`}>
                      <ShieldCheck size={14} className="inline" />
                    </button>
                  ) : (
                    <button onClick={() => revoke(c.code)} className="text-xs text-neutral-400 hover:text-red-400"
                            title="Revoke" data-testid={`cert-revoke-${c.code}`}>
                      <ShieldOff size={14} className="inline" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function LessonEditor({ lessonId, module, onSaved }) {
  const lesson = module?.lessons.find((l) => l.id === lessonId);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTitle(lesson?.title || "");
    setBody(lesson?.body || "");
    setEditing(false);
  }, [lessonId, lesson?.title, lesson?.body]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await client.patch(`/admin/lessons/${lessonId}`, { title, body });
      toast.success("Lesson saved");
      setEditing(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!lesson) return null;

  return (
    <section className="mb-2" data-testid="lesson-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline">Lesson Content</p>
          <p className="text-sm text-neutral-500 font-body mt-1">
            Rewrite the lesson title or body. Students see updates instantly.
          </p>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="btn-outline-gold" data-testid="lesson-edit-btn">
            <Edit3 size={12} className="inline mr-2" /> Edit Lesson
          </button>
        ) : (
          <div className="flex gap-3">
            <button onClick={() => { setEditing(false); setTitle(lesson.title); setBody(lesson.body); }}
                    className="btn-outline-gold" data-testid="lesson-edit-cancel">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-gold" data-testid="lesson-edit-save">
              <Save size={12} className="inline mr-2" /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 className="input-boardroom font-display text-lg" data-testid="lesson-edit-title" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={18}
                    className="input-boardroom font-body leading-relaxed resize-y"
                    data-testid="lesson-edit-body" />
          <p className="text-[10px] text-neutral-600 uppercase tracking-widest">
            Tip: Use blank lines to separate paragraphs · wrap emphasis in **double asterisks**
          </p>
        </div>
      ) : (
        <div className="border border-[#1f1f1f] bg-[#0a0a0a] p-5 max-h-64 overflow-auto no-scrollbar">
          <p className="font-display text-xl mb-3">{lesson.title}</p>
          <p className="text-sm text-neutral-400 font-body whitespace-pre-line line-clamp-6">
            {lesson.body}
          </p>
        </div>
      )}
    </section>
  );
}

function OutlookTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyOutlook());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await client.get("/outlooks");
    setItems(data.outlooks);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await client.put(`/admin/outlooks/${editingId}`, form);
        toast.success("Outlook updated");
      } else {
        await client.post("/admin/outlooks", form);
        toast.success("Outlook published to all students");
      }
      setForm(emptyOutlook());
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const edit = (o) => {
    setEditingId(o.id);
    setForm({
      title: o.title || "", week_of: o.week_of || "",
      bias: o.bias || "", focus_pairs: o.focus_pairs || "",
      key_levels: o.key_levels || "", news_events: o.news_events || "",
      setups: o.setups || "", notes: o.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this outlook?")) return;
    await client.delete(`/admin/outlooks/${id}`);
    load();
  };

  const draftAI = async () => {
    setBusy(true);
    toast("Drafting with AI — pulling live headlines + calendar…", { duration: 6000 });
    try {
      const { data } = await client.post("/admin/intel/draft-outlook");
      if (data.draft) {
        setForm({
          title: data.draft.title || "",
          week_of: data.draft.week_of || new Date().toISOString().slice(0, 10),
          bias: data.draft.bias || "",
          focus_pairs: data.draft.focus_pairs || "",
          key_levels: data.draft.key_levels || "",
          news_events: data.draft.news_events || "",
          setups: data.draft.setups || "",
          notes: data.draft.notes || "",
        });
        toast.success(`AI draft ready · used ${data.used_headlines} headlines · ${data.used_events} calendar events · review then Publish`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data.error || "Draft failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-outlook">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={16} className="text-[#D4AF37]" />
            <p className="overline">{editingId ? "Edit Outlook" : "Weekly Market Outlook Composer"}</p>
          </div>
          <h3 className="font-display text-3xl mb-3">
            {editingId ? "Editing published outlook" : "Publish this week's read"}
          </h3>
          <p className="text-sm text-neutral-500 max-w-2xl">
            Fill any field you want to include — empty sections are hidden. Students see the latest outlook under
            the <em>Outlook</em> tab in their menu, ordered by <em>week_of</em>.
          </p>
        </div>
        <button type="button" onClick={draftAI} disabled={busy}
                data-testid="outlook-draft-ai"
                className="btn-gold text-xs whitespace-nowrap">
          ◇ Draft with AI
        </button>
      </div>

      <form onSubmit={submit} className="border border-[#1f1f1f] p-5 mb-10 space-y-4">
        <div className="grid md:grid-cols-[2fr_1fr] gap-4">
          <Field label="Title" testId="outlook-title">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="e.g. NFP week: fade the raid, ride the reversal"
                   className="input-boardroom" data-testid="outlook-title-input" />
          </Field>
          <Field label="Week Of" testId="outlook-week">
            <input required type="date" value={form.week_of}
                   onChange={(e) => setForm({ ...form, week_of: e.target.value })}
                   className="input-boardroom" data-testid="outlook-week-input" />
          </Field>
        </div>
        <TextArea label="Bias" v={form.bias} onChange={(v) => setForm({ ...form, bias: v })} testId="outlook-bias-input"
                  placeholder="Overall directional bias on the week — 2-4 sentences." />
        <TextArea label="Focus Pairs / Instruments" v={form.focus_pairs} onChange={(v) => setForm({ ...form, focus_pairs: v })}
                  testId="outlook-focus-input" placeholder="e.g. EURUSD, GBPJPY, ES, XAUUSD" />
        <TextArea label="Key Levels" v={form.key_levels} onChange={(v) => setForm({ ...form, key_levels: v })}
                  testId="outlook-levels-input" placeholder="Weekly H/L, PDH/PDL, NWOG, LRLs, key OB/FVG zones." />
        <TextArea label="High-Impact News" v={form.news_events} onChange={(v) => setForm({ ...form, news_events: v })}
                  testId="outlook-news-input" placeholder="e.g. Tue CPI 13:30 GMT · Wed FOMC 19:00 GMT · Fri NFP" />
        <TextArea label="Pre-Mapped Setups" v={form.setups} onChange={(v) => setForm({ ...form, setups: v })}
                  testId="outlook-setups-input" placeholder="Setup 1: London sweep + CISD on EURUSD…" />
        <TextArea label="Additional Notes" v={form.notes} onChange={(v) => setForm({ ...form, notes: v })}
                  testId="outlook-notes-input" placeholder="Anything else — discipline reminders, macro context, etc." />
        <div className="flex gap-3">
          <button type="submit" disabled={busy} className="btn-gold" data-testid="outlook-submit">
            <Megaphone size={13} className="inline mr-2" />
            {busy ? "Saving…" : editingId ? "Update Outlook" : "Publish Outlook"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(emptyOutlook()); }}
                    className="btn-outline-gold" data-testid="outlook-cancel">Cancel Edit</button>
          )}
        </div>
      </form>

      <p className="overline mb-4">Published Outlooks ({items.length})</p>
      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.id} className="border border-[#1f1f1f] p-4 flex items-start justify-between"
               data-testid={`outlook-row-${o.id}`}>
            <div>
              <p className="font-display text-xl text-[#D4AF37]">{o.title}</p>
              <p className="text-xs text-neutral-500 mt-1 uppercase tracking-widest">
                Week of {o.week_of} · Published {new Date(o.at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(o)} className="text-neutral-400 hover:text-[#D4AF37]"
                      data-testid={`outlook-edit-${o.id}`}><Edit3 size={14} /></button>
              <button onClick={() => del(o.id)} className="text-neutral-400 hover:text-red-400"
                      data-testid={`outlook-delete-${o.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function emptyOutlook() {
  return { title: "", week_of: new Date().toISOString().slice(0, 10),
           bias: "", focus_pairs: "", key_levels: "",
           news_events: "", setups: "", notes: "" };
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="overline mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function TextArea({ label, v, onChange, placeholder, testId }) {
  return (
    <Field label={label} testId={testId}>
      <textarea value={v} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} rows={3}
                className="input-boardroom font-body resize-y" />
    </Field>
  );
}

function EmailStatusBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    client.get("/admin/email/status").then((r) => setStatus(r.data));
  }, []);
  if (!status) return null;
  return (
    <div className={`border p-4 mb-6 flex items-center gap-3 ${status.configured ? "border-[#D4AF37]/40 bg-[#0a0a0a]" : "border-neutral-800 bg-[#0a0a0a]"}`}
         data-testid="email-status-banner">
      {status.configured ? (
        <>
          <Mail size={16} className="text-[#D4AF37]" />
          <div className="text-sm">
            <span className="text-[#D4AF37] uppercase tracking-widest text-[10px]">Email Delivery Active</span>
            <span className="text-neutral-500 ml-3 font-body">
              Invites and password resets send from <span className="text-neutral-200 font-mono">{status.sender_email}</span>.
            </span>
          </div>
        </>
      ) : (
        <>
          <MailX size={16} className="text-neutral-500" />
          <div className="text-sm">
            <span className="text-neutral-400 uppercase tracking-widest text-[10px]">Email Delivery Off</span>
            <span className="text-neutral-500 ml-3 font-body">
              No Resend API key configured — temp passwords will appear in the toast so you can copy them manually.
              Add <span className="font-mono text-[#D4AF37]">RESEND_API_KEY</span> to <span className="font-mono text-neutral-300">/app/backend/.env</span> and restart to enable.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
